import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import api from '../utils/api';

// Google-Maps-style address typeahead. Type a street/place; pick a result and it returns
// { street, suburb, state, postcode, lat, lon } so the parent can fill every field + store coords.
// Free OpenStreetMap/Nominatim via the backend /api/address-search proxy.
export default function AddressAutocomplete({
  id, value, onChange, onSelect, placeholder = 'Start typing an address…', className = 'input-field',
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const skipNext = useRef(false);   // don't re-search right after a pick

  const search = useCallback((q) => {
    if (!q || q.trim().length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    api.get('/address-search', { params: { q } })
      .then(r => { setResults(Array.isArray(r.data) ? r.data : []); setOpen(true); setActive(-1); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(value || ''), 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, search]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = async (r) => {
    skipNext.current = true;
    setOpen(false); setResults([]);
    let d = r;
    // Google predictions carry only a placeId — resolve the exact address + coordinates on pick.
    if (r.needsDetails && r.id) {
      setLoading(true);
      try { const res = await api.get('/address-details', { params: { placeId: r.id } }); if (res.data) d = { ...r, ...res.data }; }
      catch { /* fall back to the prediction text */ }
      finally { setLoading(false); }
    }
    let street = d.street || d.name || (d.label ? d.label.split(',')[0] : '');
    // Geocoders (Nominatim/Google) routinely drop a unit/subpremise and return only the street
    // ("46 Wellington Road"). Preserve what the user typed so "Unit 8" / "8/46" isn't lost.
    const typed = (value || '').trim();
    // Word form: "Unit 8", "Apt 8", "Flat 2", "Suite 3", "Level 1", "Shop 4", "Villa 5".
    const wordUnit = (typed.match(/^\s*((?:unit|apartment|apt|flat|suite|ste|level|lvl|shop|villa|room|rm)\.?\s*[\w-]+)\b/i) || [])[1];
    // Slash form: "8/46", "8a/46" — the subpremise part before the slash.
    const slashUnit = (typed.match(/^\s*(\d+[a-zA-Z]?)\s*\/\s*\d/) || [])[1];
    // Bare leading number, for OSM street-level matches that lack a house number.
    const lead = (typed.match(/^(\d+[a-zA-Z]?(?:\s*[-/]\s*\d+[a-zA-Z]?)?)\b/) || [])[1];
    if (street) {
      if (wordUnit && !street.toLowerCase().includes(wordUnit.toLowerCase())) {
        street = `${wordUnit.replace(/\s+/g, ' ')}, ${street}`;
      } else if (slashUnit && !street.includes('/')) {
        street = `${slashUnit}/${street}`;
      } else if (lead && !/^\d/.test(street.trim())) {
        street = `${lead} ${street}`;
      }
    }
    onSelect?.({
      street,
      suburb: d.suburb || '', state: d.state || '', postcode: d.postcode || '',
      lat: d.lat, lon: d.lon, label: d.label,
    });
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        id={id}
        className={className}
        value={value || ''}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && <Loader2 size={15} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-auto py-1">
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(r)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2.5 ${i === active ? 'bg-primary-50' : 'hover:bg-slate-50'}`}>
                <MapPin size={15} className="text-primary-500 mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800 font-medium truncate">
                    {r.name || r.street || (r.label || '').split(',')[0]}
                  </span>
                  <span className="block text-xs text-slate-400 truncate">{r.label}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
